/**
 * FinanceSettings — hub page for all financial section settings.
 * Currently exposes:
 *   • Provider Controls (Tabby & Tamara & Stripe & Ziina)
 * Future expansion: reconciliation windows, currency rates, tax settings, etc.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Percent, ChevronRight, List, Globe, Briefcase, Receipt, ShoppingBag, Megaphone } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { usePerms } from '../../permissions/PermissionsContext';

export default function FinanceSettings() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const cards = [
    {
      id: 'gateways',
      titleAr: 'إعدادات بوابات الدفع',
      titleEn: 'Payment Gateways',
      descAr: 'تشغيل/إيقاف كل بوابة (تابي / تمارا / ستريب / زينة) لكل منصة',
      descEn: 'Enable/disable each gateway (Tabby / Tamara / Stripe / Ziina) per platform',
      icon: CreditCard,
      color: '#d4af37',
      route: '/dashboard/finance/settings/gateways',
    },
    {
      id: 'commission',
      titleAr: 'نسبة عمولة تابي وتمارا',
      titleEn: 'Tabby & Tamara Commission',
      descAr: 'نسبة العمولة المُضافة تلقائياً على مبلغ الرابط + مفتاح تفعيل/إيقاف',
      descEn: 'Commission % auto-added to link amounts + on/off toggle',
      icon: Percent,
      color: '#EF4444',
      route: '/dashboard/finance/settings/commission',
    },
    {
      id: 'dropdown-lists',
      titleAr: 'القوائم المنسدلة',
      titleEn: 'Dropdown Lists',
      descAr: 'إدارة القوائم المنسدلة المستخدمة في نماذج القسم المالي (بالعربي والإنجليزي، لكل منصة)',
      descEn: 'Manage the dropdown lists used across Finance forms (AR/EN, per platform)',
      icon: List,
      color: '#10b981',
      route: '/dashboard/finance/settings/dropdown-lists',
    },
    {
      id: 'platform-urls',
      titleAr: 'روابط المنصات',
      titleEn: 'Platform URLs',
      descAr: 'ضبط الروابط الرئيسية وصفحات استلام الدفع لكل منصة (تُستخدم بعد Tamara/Stripe/Tabby/Ziina)',
      descEn: 'Configure each platform\'s home & payment-success URLs (used after Tamara/Stripe/Tabby/Ziina)',
      icon: Globe,
      color: '#3B82F6',
      route: '/dashboard/finance/settings/platform-urls',
    },
    // iter-218m (Feb 2026) — "جهات الدفع وأسعار الصرف" moved to
    // /dashboard/finance/settings/dropdown-lists (grouped with Entity Types).
    {
      id: 'receipt-studio',
      titleAr: 'ستوديو الإيصالات',
      titleEn: 'Receipt Studio',
      descAr: 'شعار موحّد، ألوان، رقم الضريبي، وتذييل مشترك لكل إيصالات العملاء (CGC) والتجار (CGB)',
      descEn: 'Unified logo, colors, TRN & shared footer for every CGC (customer) and CGB (merchant) receipt',
      icon: Receipt,
      color: '#F5A55A',
      route: '/dashboard/finance/settings/receipt-studio',
    },
    {
      id: 'ecommerce-commission',
      titleAr: 'عمولة المتاجر الإلكترونية',
      titleEn: 'E-commerce Commission',
      descAr: 'تفعيل نسبة العمولة + رسوم إدارية ثابتة تُضاف تلقائياً إلى أسعار منتجات منصة التجار',
      descEn: 'Enable commission % + flat admin fee auto-added to merchant platform product prices',
      icon: ShoppingBag,
      color: '#D946EF',
      route: '/dashboard/finance/settings/ecommerce-commission',
    },
    {
      id: 'customer-ads-pricing',
      titleAr: 'التحكم برسوم إعلانات منصة العملاء',
      titleEn: 'Customer Platform Ads Fees',
      descAr: 'أسعار نشر إعلانات السيارات ولوحات الأرقام وأرقام الموبايل حسب المدة (1–12 شهر) — تسحبها منصة العملاء',
      descEn: 'Listing fees for car, plate & mobile-number ads by duration (1–12 months) — read by the customer platform',
      icon: Megaphone,
      color: '#38bdf8',
      route: '/dashboard/finance/settings/customer-ads-pricing',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#8B5CF6]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('إعدادات القسم المالي', 'Finance Settings')}
          </h1>
          <p className="text-white/50 text-sm">
            {t('اختر الإعداد الذي تريد ضبطه', 'Pick a settings section to configure')}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {visiblePages(cards, 'route').map((c) => (
            <motion.button
              key={c.id}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(c.route)}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/40 transition-all text-start"
              data-testid={`finance-settings-card-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${c.color}15`, border: `1px solid ${c.color}30` }}
                >
                  <c.icon className="w-7 h-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">
                    {isAr ? c.titleAr : c.titleEn}
                  </h3>
                  <p className="text-white/60 text-xs leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
                <ChevronRight className={`w-5 h-5 text-white/40 group-hover:text-[#d4af37] transition-colors ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </motion.button>
          ))}
        </motion.div>
      </main>
    </div>
  );
}
